import { Checkbox, Collapse, FormControlLabel, Tooltip } from '@mui/material';
import { useFetchPartnersApi } from 'api/mypage/partner';
import { useBulkPlanProofreadEventApi, useFetchReservedInstrumentNamesApi } from 'api/mypage/proofread_event';
import { useFetchUsersApi } from 'api/mypage/users';
import { Flex } from 'components/shared/flex';
import {
  DateField,
  MonthField,
  MultipleCheckBoxField,
  NumberField,
  SelectField,
  StringField,
} from 'components/shared/form/input';
import LinkText from 'components/shared/link_text';
import Modal, { ModalSet } from 'components/shared/modal';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import { MypageStateContext } from 'contexts/mypage_state_context';
import { ProofreadEvent, ProofreadEventBulkPatchForm } from 'entities/proofread_event';
import { useForm } from 'rac';
import React, { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MypageRouteHelper } from 'routes/mypage';
import { useEffectSkipFirst } from 'utils/hooks';

type NoPlanEventFormBulkUpdateDialogProps = {
  modalSet: ModalSet<number[]>;
  onComplete: () => void;
  proofreadPlanId: number;
  proofreadEvents: ProofreadEvent[];
};
export const NoPlanEventFormBulkUpdateDialog: React.FC<NoPlanEventFormBulkUpdateDialogProps> = (props) => {
  const navigate = useNavigate();
  const globalState = useContext(GlobalStateContext);
  const mypageState = useContext(MypageStateContext);
  const usersApi = useFetchUsersApi();
  const partnersApi = useFetchPartnersApi();

  const bulkPlanForm = useForm<ProofreadEventBulkPatchForm>({
    proofreadEventIds: props.modalSet.item,
    requireSupplier: false,
    suppliableType: 'OutsideDealer',
    approverIds: [],
  });
  const bulkPlanApi = useBulkPlanProofreadEventApi(bulkPlanForm, props.proofreadPlanId);

  const approvableUsers = useMemo(() => {
    return usersApi.response.users.filter((u) => u.id !== mypageState.currentUser.id && u.authority?.proofread);
  }, [usersApi.response.users, mypageState.currentUser.id]);

  // 選択中の機器情報（modalSet.item はモーダル未オープン時に {} で初期化されるため配列チェックする）
  const selectedEvents = useMemo(() => {
    const selectedIds = Array.isArray(props.modalSet.item) ? props.modalSet.item : [];
    return props.proofreadEvents.filter((e) => selectedIds.includes(e.id!));
  }, [props.proofreadEvents, props.modalSet.item]);
  const includesSharedInstrument = useMemo(() => selectedEvents.some((e) => e.instrument?.isShared), [selectedEvents]);
  // 全件が共有機器なら、個別「設定」と同様に外部業者委託のチェック自体を無効化する
  const allSharedInstruments = useMemo(
    () => selectedEvents.length > 0 && selectedEvents.every((e) => e.instrument?.isShared),
    [selectedEvents]
  );
  // 予約管理対象（非共有）機器 = 校正予約の作成対象。含まれる場合は校正開始日・終了日の入力欄を表示する
  const reservableEvents = useMemo(
    () => selectedEvents.filter((e) => e.instrument?.canReserve && !e.instrument?.isShared),
    [selectedEvents]
  );
  const includesReservableInstrument = reservableEvents.length > 0;

  // 校正期間の入力が揃ったら、その期間に利用予約が入っている機器名を取得する（確認ダイアログで表示）
  const reservedNamesApi = useFetchReservedInstrumentNamesApi();
  useEffect(() => {
    if (!includesReservableInstrument) return;
    const selectedIds = Array.isArray(props.modalSet.item) ? props.modalSet.item : [];
    const { startDate, endDate } = bulkPlanForm.object;
    if (!startDate || !endDate || selectedIds.length === 0) return;
    reservedNamesApi.execute({
      proofreadPlanId: props.proofreadPlanId,
      proofreadEventIds: selectedIds,
      startDate,
      endDate,
    });
  }, [bulkPlanForm.object.startDate, bulkPlanForm.object.endDate, props.modalSet.item, includesReservableInstrument]);

  const handleSubmit = () => {
    let message = includesReservableInstrument
      ? '選択した校正計画を一括で設定します。\n校正期間に含まれる予約はキャンセルされますが、よろしいですか？'
      : '選択した校正計画を一括で設定します。設定後は次のステータスへ移動します。よろしいですか？';
    const reservedNames = reservedNamesApi.response.instrumentNames || [];
    if (includesReservableInstrument && reservedNames.length > 0) {
      message += `\nなお、設定する校正開始日〜終了日の期間にすでに予約がある機器は以下のとおりです。\n${reservedNames
        .map((name) => `・${name}`)
        .join('\n')}`;
    }
    globalState.confirm(message, (e) => {
      if (e === 'ok') {
        bulkPlanApi.execute();
      }
    });
  };

  const handleClickSettingLink = () => {
    navigate(`${MypageRouteHelper.settingShow()}`);
  };

  useEffect(() => {
    if (!props.modalSet.modalState.open) return;
    usersApi.execute();
    partnersApi.execute();
  }, [props.modalSet.modalState.open]);

  useEffectSkipFirst(() => {
    bulkPlanForm.update((f) => {
      f.proofreadEventIds = props.modalSet.item;
      // 全件共有機器なら外部業者委託の残留値を解除
      if (allSharedInstruments) {
        f.requireSupplier = false;
        f.suppliableId = undefined;
      }
    });
  }, [props.modalSet.item, allSharedInstruments]);

  useEffectSkipFirst(() => {
    if (!bulkPlanApi.isSuccess()) return;
    props.onComplete();
    mypageState.reloadUser();
  }, [bulkPlanApi.loading]);

  return (
    <Modal
      modalId="event-planning-bulk-form"
      modalState={props.modalSet.modalState}
      title="校正計画設定の一括更新"
      footerStyle={{ display: 'inherit' }}
      footer={
        <Flex alignItems="flex-end" justifyContent="flex-end">
          <ThemedButton color="secondary" onClick={props.modalSet.closeModal}>
            閉じる
          </ThemedButton>
          <ThemedButton color="primary" onClick={handleSubmit}>
            設定
          </ThemedButton>
        </Flex>
      }
    >
      <Flex justifyContent="space-between">
        <MonthField label="校正月" form={bulkPlanForm} attr="scheduledDate" apiError={bulkPlanApi.apiError} required />
        <NumberField label="校正価格" form={bulkPlanForm} attr="price" apiError={bulkPlanApi.apiError} />
        <SelectField
          label="担当者"
          form={bulkPlanForm}
          attr="userId"
          apiError={bulkPlanApi.apiError}
          required
          includeBlank
          data={usersApi.response.users.map((u) => ({ label: `${u.familyName} ${u.givenName}`, value: u.id }))}
          labelId="user-id"
        />
      </Flex>

      {/* 外部業者委託 */}
      <Tooltip title={allSharedInstruments ? '機器が共有中なので外部業者に委託できません' : ''}>
        <FormControlLabel
          control={
            <Checkbox
              checked={bulkPlanForm.object.requireSupplier}
              onChange={(e) => {
                bulkPlanForm.update((f) => (f.requireSupplier = e.target.checked));
              }}
              disabled={allSharedInstruments}
            />
          }
          label="外部業者に委託する"
        />
      </Tooltip>
      <Collapse in={bulkPlanForm.object.requireSupplier}>
        {includesSharedInstrument && (
          <p style={{ color: '#666', fontSize: 12, margin: '0 0 8px' }}>
            機器が共有中の場合は外部業者に委託できません（共有先の業者が自動で設定されます）。
          </p>
        )}
        <Flex>
          <SelectField
            form={bulkPlanForm}
            labelId="proofread-supplier-id-label"
            attr="suppliableId"
            label="外部業者"
            includeBlank
            data={(partnersApi.response.outsideDealers || []).map((p) => ({ value: p.id!, label: p.name! }))}
            apiError={bulkPlanApi.apiError}
            required={bulkPlanForm.object.requireSupplier}
            disabled={!bulkPlanForm.object.requireSupplier}
          />
        </Flex>
      </Collapse>

      {/* 校正予約 */}
      {includesReservableInstrument && (
        <>
          <p style={{ color: 'red', marginBottom: 16 }}>予約管理対象機器が含まれるため、校正予約を作成します</p>
          <Flex>
            <DateField
              form={bulkPlanForm}
              attr="startDate"
              label="校正開始日"
              apiError={bulkPlanApi.apiError}
              required
            />
            <DateField form={bulkPlanForm} attr="endDate" label="校正終了日" apiError={bulkPlanApi.apiError} required />
          </Flex>
        </>
      )}

      {/* 計画時添付ファイル */}
      <div style={{ margin: '12px 0' }}>
        <label>計画時添付ファイル</label>
        <Tooltip title="ファイル添付を希望の場合は、機器ごとの「設定」から添付してください">
          <div
            style={{
              marginTop: 4,
              padding: '10px 12px',
              border: '1px dashed #ccc',
              borderRadius: 4,
              color: '#9e9e9e',
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed',
              userSelect: 'none',
            }}
          >
            一括設定ではファイルを添付できません
          </div>
        </Tooltip>
      </div>

      {/* 承認者 */}
      <div style={{ margin: '12px 0' }}>
        <label>承認者を選択してください</label>
        <Flex flexWrap="wrap">
          {approvableUsers.length > 0 ? (
            approvableUsers.map((user) => (
              <MultipleCheckBoxField
                label={`${user.familyName} ${user.givenName}`}
                form={bulkPlanForm}
                attr="approverIds"
                checked={bulkPlanForm.object.approverIds!.includes(user.id!)}
                apiError={bulkPlanApi.apiError}
                id={user.id!}
                key={user.id}
              />
            ))
          ) : (
            <div>
              <p>校正を承認可能なユーザがいません。</p>
              <p>
                承認者が必要な場合は
                <LinkText inline onClick={handleClickSettingLink}>
                  設定
                </LinkText>
                から「校正履歴の閲覧・編集の権限」を設定してください。
              </p>
            </div>
          )}
        </Flex>
      </div>

      <StringField label="計画メモ" form={bulkPlanForm} attr="noteOnPlan" apiError={bulkPlanApi.apiError} multiline />
    </Modal>
  );
};

export default NoPlanEventFormBulkUpdateDialog;
